import { useCallback, useMemo, useRef, useState } from 'react';
import WatchlistUploader from '../../watchlist_table/WatchlistUploader';
import WatchlistM365SyncControl from '../../watchlist_table/WatchlistM365SyncControl';
import PosSizingUploader from '../../pos_sizing_table/PosSizingUploader';
import DraftFreshnessHeaderControl from '../../pos_sizing_table/DraftFreshnessHeaderControl';

export default function useDashboardUploadControls({
    posSizingDraftFreshnessSummary,
    handleSyncPosSizingDraft,
    posSizingLoading,
    posSizingDraftSyncLoading,
    priceRefreshLoading,
    posSizingSyncStatusLoading,
    posSizingFreshnessDetailsOpen,
    handlePosSizingFreshnessDetailsOpenChange,
    activeView,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    buildPosSizingPublicationFields,
    handlePosSizingUploadSuccess,
    handlePortfolioGuideInteraction,
    handleWatchlistGuideInteraction,
    setWatchlistUploadBusy,
    watchlistActive = true,
    watchlistOperationActive = false,
}) {
    const [posSizingUploadBusy, setPosSizingUploadBusy] = useState(false);
    const [watchlistReviewHandoff, setWatchlistReviewHandoff] = useState(null);
    const watchlistReviewCommandRef = useRef(0);
    const handleWatchlistReviewHandoff = useCallback((handoff) => {
        watchlistReviewCommandRef.current += 1;
        setWatchlistReviewHandoff({
            ...handoff,
            commandId: watchlistReviewCommandRef.current,
        });
    }, []);
    const handleWatchlistReviewHandoffConsumed = useCallback((commandId) => {
        setWatchlistReviewHandoff((current) => (
            current?.commandId === commandId ? null : current
        ));
    }, []);
    const competingPosSizingOperation = (
        posSizingLoading || posSizingDraftSyncLoading || priceRefreshLoading
    );
    const posSizingSyncDisabled = (
        competingPosSizingOperation
        || posSizingSyncStatusLoading
        || posSizingUploadBusy
        || watchlistOperationActive
    );
    const posSizingOptimizerContextControl = useMemo(() => (
        posSizingDraftFreshnessSummary.visible ? (
            <DraftFreshnessHeaderControl
                summary={posSizingDraftFreshnessSummary}
                onSyncClick={handleSyncPosSizingDraft}
                syncDisabled={posSizingSyncDisabled}
                syncInProgress={
                    posSizingLoading || posSizingDraftSyncLoading || posSizingSyncStatusLoading
                }
                detailsOpen={
                    posSizingFreshnessDetailsOpen && activeView === 'optimizer'
                }
                onDetailsOpenChange={handlePosSizingFreshnessDetailsOpenChange}
                presentation="optimizer"
            />
        ) : null
    ), [
        activeView,
        handlePosSizingFreshnessDetailsOpenChange,
        handleSyncPosSizingDraft,
        competingPosSizingOperation,
        posSizingDraftFreshnessSummary,
        posSizingDraftSyncLoading,
        posSizingFreshnessDetailsOpen,
        posSizingLoading,
        posSizingUploadBusy,
        posSizingSyncStatusLoading,
        watchlistOperationActive,
    ]);

    // Uploads also work without a saved draft: a Watchlist-only user's first
    // upload creates it.
    const posSizingUploadControl = useMemo(() => (
        !competingPosSizingOperation ? (
            <PosSizingUploader
                buildPublicationFields={buildPosSizingPublicationFields}
                onUploadSuccess={handlePosSizingUploadSuccess}
                onUploadBusyChange={setPosSizingUploadBusy}
                variant="expanded"
                onInteraction={handlePortfolioGuideInteraction}
            />
        ) : null
    ), [
        buildPosSizingPublicationFields,
        handlePortfolioGuideInteraction,
        handlePosSizingUploadSuccess,
        competingPosSizingOperation,
    ]);

    const posSizingImportsControl = useMemo(() => (
        <DraftFreshnessHeaderControl
            summary={posSizingDraftFreshnessSummary}
            onSyncClick={handleSyncPosSizingDraft}
            syncDisabled={posSizingSyncDisabled}
            syncInProgress={
                posSizingLoading || posSizingDraftSyncLoading || posSizingSyncStatusLoading
            }
            detailsOpen={
                posSizingFreshnessDetailsOpen && activeView === 'pos-sizing'
            }
            onDetailsOpenChange={handlePosSizingFreshnessDetailsOpenChange}
            triggerVariant="imports"
            headerControl={posSizingUploadControl}
        />
    ), [
        handlePosSizingFreshnessDetailsOpenChange,
        handleSyncPosSizingDraft,
        competingPosSizingOperation,
        posSizingDraftSyncLoading,
        posSizingFreshnessDetailsOpen,
        posSizingDraftFreshnessSummary,
        posSizingLoading,
        posSizingUploadBusy,
        posSizingSyncStatusLoading,
        posSizingUploadControl,
        watchlistOperationActive,
        activeView,
    ]);

    const watchlistUploadControl = useMemo(() => (
        <WatchlistUploader
            active={watchlistActive}
            default_target_vol_pct={defaultTargetVolPct}
            default_target_vol_dollar={defaultTargetVolDollar}
            default_scaler={defaultScaler}
            variant="expanded"
            onInteraction={handleWatchlistGuideInteraction}
            onUploadBusyChange={setWatchlistUploadBusy}
            externalReviewHandoff={watchlistReviewHandoff}
            onExternalReviewHandoffConsumed={handleWatchlistReviewHandoffConsumed}
        />
    ), [
        defaultScaler,
        defaultTargetVolDollar,
        defaultTargetVolPct,
        handleWatchlistGuideInteraction,
        handleWatchlistReviewHandoffConsumed,
        setWatchlistUploadBusy,
        watchlistActive,
        watchlistReviewHandoff,
    ]);

    const watchlistM365SyncControl = useMemo(() => (
        <WatchlistM365SyncControl
            onReviewHandoff={handleWatchlistReviewHandoff}
        />
    ), [handleWatchlistReviewHandoff]);

    return {
        posSizingOptimizerContextControl,
        posSizingImportsControl,
        watchlistUploadControl,
        watchlistM365SyncControl,
        watchlistReviewHandoff,
        handleWatchlistReviewHandoff,
    };
}
